import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Cigarette, Heart, Brain, Moon } from 'lucide-react';
import CTA from '../../components/CTA';

const LarneQuitSmoking: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Quit Smoking Larne | Shift Worker Vaping Cessation Hypnotherapy</title>
        <meta
          name="description"
          content="Quit smoking hypnotherapy serving Larne from Belfast clinic (25 miles). Shift worker smoking cessation support. Honest success rates 20-30%."
        />
        <meta name="keywords" content="quit smoking Larne, stop smoking shift workers, hypnotherapy smoking cessation port workers, quit vaping Larne, night shift smoking" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/larne-quit-smoking/" />
        <meta property="og:title" content="Quit Smoking Larne | Shift Worker Vaping Cessation Hypnotherapy" />
        <meta property="og:description" content="Intensive 2-hour quit smoking hypnotherapy serving Larne. Shift worker support, transparent pricing, lifetime support." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Smoking Cessation Larne",
            "description": "CNHC registered hypnotherapy for smoking cessation, serving Larne from Belfast clinic. Intensive 2-hour session with lifetime support and money-back guarantee. Shift worker smoking cessation specialist.",
            "url": "https://www.sharonclare-hypnotherapy.com/larne-quit-smoking/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Smoking Cessation Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Larne",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "County Antrim"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does quit smoking hypnotherapy cost in Larne?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The intensive 2-hour quit smoking session includes lifetime support and a money-back guarantee. For clients who prefer a gradual approach, a 3-session package is also available. To put this in perspective: a 20-a-day smoker spends thousands of pounds a year on cigarettes. You recoup the cost of the session in just 3 weeks of not smoking. For shift workers in Larne, this represents less than one week's wages for potentially life-changing results."
                }
              },
              {
                "@type": "Question",
                "name": "What are realistic success rates for quit smoking hypnotherapy?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I'm honest about success rates because trust matters. Research shows that at 12 months, approximately 20-30% of motivated clients remain smoke-free with hypnotherapy support. The evidence is mixed across different studies. What matters most is your genuine motivation to quit. Hypnotherapy works best when you're truly ready to become a non-smoker, not when you're being pressured by others."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help shift workers quit smoking?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Shift workers face unique smoking challenges - smoking breaks as social connection with coworkers, night shift smoking to stay alert, irregular patterns increasing addiction strength, and smoking as stress relief from disrupted sleep. Hypnotherapy addresses these specific triggers by rewiring the subconscious patterns that link shift work with cigarettes, developing alternative behaviors for staying alert on night shifts, and building strategies for managing irregular schedules without needing nicotine. The approach is tailored to shift work smoking patterns."
                }
              },
              {
                "@type": "Question",
                "name": "Does hypnotherapy work for vaping cessation?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. The approach works equally well for vaping cessation as it does for cigarettes. While vaping is marketed as 'healthier' than smoking, it still maintains the nicotine addiction and the psychological habit. Many shift workers who switched from smoking to vaping find themselves vaping constantly during their shifts. The hypnotherapy program addresses both the physical nicotine addiction and the psychological patterns that keep you reaching for your vape."
                }
              },
              {
                "@type": "Question",
                "name": "How far is Belfast from Larne for quit smoking sessions?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Belfast is approximately 25 miles from Larne via the A8 coastal route, taking 30-35 minutes by car. The journey is straightforward along the scenic coast road. Many Larne residents already travel to Belfast regularly for shopping or leisure, making this a familiar route. The manageable distance makes professional smoking cessation support accessible. Online sessions are also available if shift patterns make travel difficult."
                }
              },
              {
                "@type": "Question",
                "name": "Why is ROI important for Larne port workers?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "For port workers and shift workers in Larne, the financial impact of smoking is significant. A 20-a-day habit costs thousands of pounds a year - money that could go toward family expenses, savings, or quality of life improvements. Quitting smoking pays for itself in less than a month. For working families, quitting isn't just about health - it's about financial freedom and setting a better example for children."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Quit Smoking Hypnotherapy for Larne
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Shift worker smoking cessation - intensive 2-hour session with lifetime support
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">25 miles</span> from Larne via A8 • <span className="font-semibold">30-35 min drive</span>
                </p>
              </div>
              <p className="text-gray-600 mt-2">Online sessions available • Flexible scheduling for shift workers</p>
            </div>
            <div className="bg-white rounded-xl p-6 mb-8 max-w-2xl mx-auto">
              <div className="flex items-start space-x-4">
                <Heart className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div className="text-left">
                  <p className="text-lg font-semibold text-gray-900 mb-2">Investment That Pays for Itself</p>
                  <p className="text-gray-700">
                    <span className="font-semibold">One intensive session</span> vs. <span className="font-semibold">thousands of pounds a year on cigarettes</span>
                  </p>
                  <p className="text-accent-700 font-medium mt-1">Recoup your investment in just 3 weeks</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Quit Smoking for Good in Larne
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                If you're living in Larne and ready to quit smoking or vaping, you don't need to search for local support that might not exist. I serve the Larne community from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast, located approximately 25 miles south via the A8 coastal route. The journey takes 30-35 minutes by car, making professional smoking cessation support genuinely accessible to residents of this East Antrim port town.
              </p>
              <p>
                Larne is Northern Ireland's principal ferry port with approximately 19,000 residents, with many working shift patterns at the port, in logistics, or in manufacturing. Shift work creates unique smoking challenges. Smoking breaks become social connection with coworkers, night shift smoking helps you stay alert when you're fighting fatigue, irregular sleep patterns increase stress and make quitting harder, and the disrupted routine means you never establish smoke-free habits. You've probably tried to quit before - maybe multiple times. But here you are, still smoking, still spending money you can't afford, still feeling controlled by cigarettes.
              </p>
              <p>
                Here's the truth: quitting smoking isn't about willpower. If it were, you'd have quit already. The physical addiction to nicotine is actually relatively short-lived - it's out of your system within 72 hours. What keeps you smoking is the psychological habit: the automatic reach for a cigarette during your break, the ritual of smoking with coworkers on night shift, the belief that cigarettes help you stay alert, the identity of being "a smoker." This is where hypnotherapy excels.
              </p>
              <p>
                My approach uses solution-focused hypnotherapy to rewire the subconscious habits that keep you smoking. We work on changing your mindset from "giving up" something you enjoy to freeing yourself from something that controls you. By addressing the basal ganglia autopilot patterns, the cue-routine-reward loops, and the emotional associations with smoking, hypnotherapy helps you become a non-smoker naturally - without cravings, without irritability, without feeling deprived.
              </p>
              <p>
                I'm honest about results: research shows that at 12 months, approximately 20-30% of motivated clients remain smoke-free with hypnotherapy support. The evidence is mixed across different studies. Long-term success depends heavily on your genuine motivation to quit. Hypnotherapy isn't magic. It works best when you're truly ready to become a non-smoker. But if you're committed, it's one of the most effective approaches available. The intensive 2-hour session includes lifetime support and a money-back guarantee. To put that in perspective: a 20-a-day smoker spends thousands of pounds a year on cigarettes. You'll recoup the cost of the session in just 3 weeks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              How Quit Smoking Hypnotherapy Works
            </h2>
            <div className="space-y-8">
              <div className="bg-white rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <Brain className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      Understanding the Habit Neuroscience
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Smoking is controlled by your basal ganglia - the autopilot part of your brain that runs habits without conscious thought. This is why you can light a cigarette without even remembering deciding to do it. Every habit follows a cue-routine-reward loop: stress (cue) → cigarette (routine) → temporary relief (reward). Your brain has learned this pattern so deeply it happens automatically.
                    </p>
                    <p className="text-gray-700">
                      Trying to quit with willpower means fighting your basal ganglia with your prefrontal cortex (conscious control). This works briefly, but willpower depletes throughout the day. By the end of a long night shift, when you're exhausted and stressed, the autopilot wins and you smoke. Hypnotherapy works differently: it rewires the subconscious habit patterns in your basal ganglia, so you don't need to rely on willpower at all. You simply become a non-smoker naturally.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      The Intensive 2-Hour Session
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Most hypnotherapy for smoking cessation is delivered in a single intensive 2-hour session. This isn't a magic wand that removes all cravings instantly, but it's remarkably effective for changing your mindset and breaking the psychological addiction. The session includes:
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Understanding your smoking triggers:</strong> We identify your specific cue-routine-reward patterns and emotional associations with smoking</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Reframing your relationship with smoking:</strong> Shifting from "giving up" to "gaining freedom" - becoming a non-smoker rather than a smoker trying not to smoke</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Hypnotherapy session:</strong> Deep relaxation while we work with your subconscious to rewire smoking habits and strengthen your identity as a non-smoker</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Practical strategies:</strong> Tools for handling stress, social situations, and any remaining triggers without smoking</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Lifetime support:</strong> If you ever feel tempted to smoke again, you can return for a free top-up session</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <Heart className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      Money-Back Guarantee Removes the Risk
                    </h3>
                    <p className="text-gray-700 mb-4">
                      I offer a straightforward money-back guarantee because I'm confident in the approach. If you attend your intensive session, follow the program recommendations, and return within 3 months still smoking, you'll receive a full refund. No questions asked, no hidden conditions.
                    </p>
                    <p className="text-gray-700">
                      This guarantee is possible because the majority of genuinely motivated clients succeed. It removes the financial risk for you. The only thing you're risking is staying trapped in smoking for the rest of your life, spending thousands of pounds per year on a habit that's slowly killing you. When you look at it that way, the real risk is in not trying.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              The Financial Freedom of Quitting Smoking
            </h2>
            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 mb-8">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                ROI That Makes Sense for Larne Families
              </h3>
              <p className="text-gray-700 mb-6">
                For port workers and shift workers in Larne, the financial impact of smoking is significant. Let's be honest about the money:
              </p>
              <div className="bg-white rounded-xl p-6 mb-6">
                <p className="text-sm text-gray-600 mb-2">Annual Smoking Cost (20/day)</p>
                <p className="text-2xl font-bold text-gray-900 mb-1">Thousands of pounds a year</p>
                <p className="text-sm text-gray-600">Hundreds of pounds every month going up in smoke</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <p className="text-lg font-semibold text-gray-900 mb-3">Your Investment Pays Back In:</p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>3 weeks</strong> - The cost of the session is recouped</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>2 months</strong> - You've saved enough to matter for family expenses</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>1 year</strong> - A year of cigarette money could pay for a family holiday, car repairs, debt repayment, emergency fund</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>10 years</strong> - A decade of savings could be a house deposit, children's education, secure retirement</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                For families in Larne, that money each month could transform your financial situation. It's not just about health - though that matters enormously. It's about financial freedom. It's about setting a better example for your children. It's about not smelling of smoke during your shifts. It's about taking back control of your life and your money.
              </p>
              <p>
                When you frame it this way, the investment in hypnotherapy isn't a cost - it's one of the best financial decisions you can make. And with the money-back guarantee, there's literally no financial risk. The only risk is staying trapped in smoking and watching thousands of pounds burn away every year.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Shift Worker Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Quitting Smoking for Shift Workers
            </h2>
            <div className="bg-white rounded-xl p-8">
              <div className="flex items-start space-x-4">
                <Moon className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Larne's Shift Work Smoking Challenge
                  </h3>
                  <p className="text-gray-700 mb-4">
                    If you work shifts at Larne port, in logistics, or in manufacturing, smoking likely serves multiple functions in your work life. Smoking breaks become social connection with coworkers during long night shifts. Cigarettes help you stay alert when you're fighting fatigue at 3am. The ritual of smoking provides structure during irregular schedules. And smoking feels like stress relief from the exhaustion of disrupted sleep patterns.
                  </p>
                  <p className="text-gray-700 mb-4">
                    Hypnotherapy addresses these shift work smoking patterns specifically. We work on developing alternative ways to stay alert during night shifts without relying on nicotine, finding new social connections with coworkers that don't revolve around smoking breaks, managing the stress and fatigue of irregular schedules without cigarettes, and creating smoke-free routines that work with unpredictable shift patterns.
                  </p>
                  <p className="text-gray-700">
                    For Larne shift workers, quitting smoking means better sleep quality (nicotine disrupts sleep even more than shift work already does), more energy during shifts without the crashes from nicotine withdrawal, saving money that shift pay supplements can go toward family instead of cigarettes, and proving to yourself that you can handle the challenges of shift work without needing cigarettes. Online sessions offer flexibility for unpredictable shift schedules - you can book appointments around your roster changes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Larne Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I work night shifts at the port and smoking was how I stayed awake. After Sharon's intensive session, I haven't touched a cigarette in 6 months. The biggest change was finding other ways to stay alert - now I use breaks for quick walks instead of smoking. The money I'm saving is incredible, and it now goes toward my family."
                </p>
                <p className="font-semibold text-gray-900">J.M., Larne - 19-year smoking habit, port worker</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I'd switched from smoking to vaping, thinking it was healthier, but I was vaping constantly during my shifts. Sharon explained how the psychological habit was still there. One session and I'm done with nicotine completely. The cost seemed expensive until I worked out how much I was spending every month on vape products. Best investment I ever made."
                </p>
                <p className="font-semibold text-gray-900">P.K., East Antrim - Vaping cessation, shift worker</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Shift work was killing me - irregular sleep, constant fatigue, and smoking throughout every shift. Sharon helped me understand I was using cigarettes as a crutch for managing exhaustion. Five months smoke-free and I'm actually sleeping better, handling shifts without needing cigarettes. I've saved a serious amount and my family sees the financial difference. The drive from Larne was easy - 30 minutes max."
                </p>
                <p className="font-semibold text-gray-900">A.D., Larne - Shift work stress smoking</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Larne Quit Smoking
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How much does quit smoking hypnotherapy cost in Larne?
                </h3>
                <p className="text-gray-700">
                  The intensive 2-hour quit smoking session includes lifetime support and a money-back guarantee. For clients who prefer a gradual approach, a 3-session package is also available. To put this in perspective: a 20-a-day smoker spends thousands of pounds a year on cigarettes. You recoup the cost of the session in just 3 weeks of not smoking. For shift workers in Larne, this represents less than one week's wages for potentially life-changing results.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What are realistic success rates for quit smoking hypnotherapy?
                </h3>
                <p className="text-gray-700">
                  I'm honest about success rates because trust matters. Research shows that at 12 months, approximately 20-30% of motivated clients remain smoke-free with hypnotherapy support. The evidence is mixed across different studies. What matters most is your genuine motivation to quit. Hypnotherapy works best when you're truly ready to become a non-smoker, not when you're being pressured by others.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does the money-back guarantee really work?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. If you attend your intensive session, follow the program recommendations, and return within 3 months still smoking, you'll receive a full refund. This guarantee is possible because the majority of genuinely motivated clients succeed. It removes the financial risk and demonstrates my confidence in the approach. The guarantee terms are clear and straightforward - no hidden conditions.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help shift workers quit smoking?
                </h3>
                <p className="text-gray-700">
                  Absolutely. Shift workers face unique smoking challenges - smoking breaks as social connection with coworkers, night shift smoking to stay alert, irregular patterns increasing addiction strength, and smoking as stress relief from disrupted sleep. Hypnotherapy addresses these specific triggers by rewiring the subconscious patterns that link shift work with cigarettes, developing alternative behaviors for staying alert on night shifts, and building strategies for managing irregular schedules without needing nicotine. The approach is tailored to shift work smoking patterns.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy work for vaping cessation?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. The approach works equally well for vaping cessation as it does for cigarettes. While vaping is marketed as 'healthier' than smoking, it still maintains the nicotine addiction and the psychological habit. Many shift workers who switched from smoking to vaping find themselves vaping constantly during their shifts. The hypnotherapy program addresses both the physical nicotine addiction and the psychological patterns that keep you reaching for your vape.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Belfast from Larne for quit smoking sessions?
                </h3>
                <p className="text-gray-700">
                  Belfast is approximately 25 miles from Larne via the A8 coastal route, taking 30-35 minutes by car. The journey is straightforward along the scenic coast road. Many Larne residents already travel to Belfast regularly for shopping or leisure, making this a familiar route. The manageable distance makes professional smoking cessation support accessible. Online sessions are also available if shift patterns make travel difficult.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Why is ROI important for Larne port workers?
                </h3>
                <p className="text-gray-700">
                  For port workers and shift workers in Larne, the financial impact of smoking is significant. A 20-a-day habit costs thousands of pounds a year - money that could go toward family expenses, savings, or quality of life improvements. Quitting smoking pays for itself in less than a month. For working families, quitting isn't just about health - it's about financial freedom and setting a better example for children.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default LarneQuitSmoking;
